import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { Banner } from '@ingenico/react-ui/banner';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import {
  DATAGRID_ROWS_PER_PAGE_OPTIONS,
  DataGridCore,
  ListViewContainer,
  ListViewHeader,
} from '@ingenico/react-ui/datagrid';
import { SearchBarInput } from '@ingenico/react-ui/search-bar-input';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { LogicalDeviceApplicationsCardProps } from './logicalDeviceApplicationsCard.model';
import styles from './logicalDeviceApplicationsCard.module.scss';
import { getRowId } from './logicalDeviceApplicationsCard.util';

export function LogicalDeviceApplicationsCard({
  grid,
  searchInput,
  isApplicationError,
  appsError,
  getErrorMessage,
  isFirstLoading,
}: LogicalDeviceApplicationsCardProps): ReactNode {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE, ALL_LNGS_NAMESPACES.COMMON]);

  if (appsError?.status === 404 || isFirstLoading) {
    return null;
  }

  return (
    <ListViewContainer>
      <div className={styles.titleContainer}>
        <CoreTypography variant={CoreTypographyVariant.Title02Bold}>{t('softwareList.title')}</CoreTypography>
      </div>
      {isApplicationError ? (
        <div className={styles.errorBanner}>
          <Banner
            message={appsError ? getErrorMessage(appsError) : t('error.default', { ns: ALL_LNGS_NAMESPACES.COMMON })}
          />
        </div>
      ) : (
        <>
          <ListViewHeader
            customClassName={styles.listViewHeaderContainer}
            leftSlot={
              <div className={styles.leftSlot__container}>
                <SearchBarInput
                  onSearchChange={searchInput.handleChange}
                  placeholder={t('search.placeholder', { ns: ALL_LNGS_NAMESPACES.COMMON })}
                  value={searchInput.value}
                  variant="filled"
                />
              </div>
            }
          />
          <DataGridCore
            columnBufferPx={8}
            columns={grid.columns}
            getRowId={getRowId}
            headerHeightPixels={395}
            initialState={{
              pagination: { paginationModel: { pageSize: DATAGRID_ROWS_PER_PAGE_OPTIONS[0], page: 0 } },
            }}
            noRowsProps={{
              image: 'empty-services',
              messageLabel: 'emptyListOrPage.message',
              namespace: ALL_LNGS_NAMESPACES.COMMON,
              titleLabel: 'emptyListOrPage.title',
              isBordered: false,
            }}
            paginationMode="client"
            rows={grid.rows}
            pageSizeOptions={DATAGRID_ROWS_PER_PAGE_OPTIONS}
            sortingMode="client"
          />
        </>
      )}
    </ListViewContainer>
  );
}
